import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { generateInitials } from '../../utils/userUtils';
import { navigateToLogin } from '../../utils/navigationUtils';
import { NotificationBell } from '../../components/NotificationBell';
import AiSOP from '../../assets/icons/AiSOP.svg';

// Header Profile Component
export function HeaderProfile({ onProfileClick }: { onProfileClick: () => void }) {
    const [open, setOpen] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        function onClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        window.addEventListener('mousedown', onClickOutside);
        return () => window.removeEventListener('mousedown', onClickOutside);
    }, []);

    return (
        <div ref={containerRef} className="position-relative">
            <button type="button" onClick={() => setOpen((v) => !v)} className="d-flex align-items-center gap-3 border-0 bg-transparent">
                <div className="user-profile-circle">
                    {user?.full_name ? generateInitials(user.full_name) : 'W'}
                </div>
                <h4 className="mb-0 grey-700 d-none d-md-block">{user?.full_name || 'Worker'}</h4>
            </button>

            {/* Mobile Overlay */}
            {open && (
                <div
                    className={`mobile-profile-overlay ${open ? 'show' : ''}`}
                    onClick={() => setOpen(false)}
                />
            )}

            {open && (
                <div className={`header-profile-dropdown ${open ? 'show' : ''}`}>
                    <div className="px-4 py-3">
                        <p className="grey-800 text-16-bold mb-0">{user?.full_name || 'Worker'}</p>
                        <p className="grey-600 text-16-reg mb-0">{user?.email ?? 'worker@renegade.com'}</p>
                    </div>
                    <div className="border-top" />
                    <button onClick={() => { onProfileClick(); setOpen(false); }} className="w-100 d-flex align-items-center gap-3 px-4 py-3 border-0 bg-transparent text-start profile-dropdown-btn">
                        <span className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                            <i className="iconoir-user text-20-reg"></i>
                        </span>
                        <span className="text-16-bold grey-900">Profile</span>
                    </button>
                    <div className="border-top" />
                    <button onClick={() => { setShowLogoutModal(true); setOpen(false); }} className="w-100 d-flex align-items-center gap-3 px-4 py-3 border-0 bg-transparent text-start logout-dropdown-btn">
                        <span className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', backgroundColor: '#f8d7da' }}>
                            <img src="/icons/signOut.png" alt="Sign out" style={{ width: '16px', height: '16px' }} />
                        </span>
                        <span className="text-16-bold color-renegade">Sign out</span>
                    </button>
                </div>
            )}

            {/* Logout Confirmation Modal */}
            <LogoutConfirmationModal
                show={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                onConfirm={() => {
                    logout();
                    setShowLogoutModal(false);
                    toast.success("Signed out successfully!");
                    navigateToLogin(navigate);
                }}
            />
        </div>
    );
}

// Logout Confirmation Modal Component
function LogoutConfirmationModal({
    show,
    onClose,
    onConfirm
}: {
    show: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) {
    return (
        <Modal show={show} onHide={onClose} className='mini-modal-width' centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Sign Out</Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <div className="text-left">
                    <p className="text-gray-600 mb-6">Are you sure you want to sign out?</p>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-3">
                        <button
                            onClick={onClose}
                            className="renegade-btn-secondary mini-btn"
                        >
                            No
                        </button>
                        <button
                            onClick={onConfirm}
                            className="renegade-btn-primary mini-btn"
                        >
                            Yes
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Header Worker Component
export function HeaderWorker({ onProfileClick, onMobileMenuClick }: { onProfileClick: () => void; onMobileMenuClick?: () => void }) {
    return (
        <div className="worker-header-card d-flex justify-content-between align-items-center flex-shrink-0">
            <div className="d-flex align-items-center gap-20">
                {/* Hamburger Menu for Mobile */}
                <button
                    className="mobile-hamburger-btn d-none"
                    onClick={onMobileMenuClick}
                    aria-label="Toggle menu"
                >
                    <svg width="29" height="16" viewBox="0 0 29 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="29" height="6" rx="3" fill="#DA291C" />
                        <rect y="10" width="18" height="6" rx="3" fill="#DA291C" />
                    </svg>

                </button>

                {/* Desktop Title */}
                <h3 className="grey-700 d-flex align-items-center gap-3 desktop-title">
                    <img src={AiSOP} />
                    <span className="color-renegade">SOP Assistant </span>
                    For AI Field Support
                </h3>
            </div>
            <div className="d-flex align-items-center gap-20">
                <NotificationBell />
                <HeaderProfile onProfileClick={onProfileClick} />
            </div>
        </div>
    );
}
